<template>
  <v-container>
    <app-modal v-model="activate" width="500" min-height="600" persistent :showCard="false">
      <template v-slot:extra>
        <v-window v-model="window" v-if="showSelectStrategy">
          <v-window-item key="primary" value="primary">
            <form-template title="Edit Guideline Calculation Strategy" ref="child-component" :proceed-button-text="formHasNext ? 'Next' : 'Save'" :enable-submit="isValidForm" @save="clickSaveForm" @cancel="clickClearForm">
              <v-form ref="form" style="width: 100%">
                <v-flex xs12>
                  <v-select v-model="selectedAlgorithm" :items="strategyOptions" :loading="isLoading" label="Choose a CoFactor Calculation Strategy" outline></v-select>
                </v-flex>
                <v-flex xs12 v-if="selectedAlgorithmObject && selectedAlgorithmObject.Description">Description:
                  {{ selectedAlgorithmObject.Description }}</v-flex>
              </v-form>
            </form-template>
          </v-window-item>
        </v-window>
        <v-card v-if="addDefaultValue">
          <v-toolbar dense dark color="primary">
            <v-toolbar-title>Add CoFactor Values</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12>Please provide values and units for the cofactors below.</v-flex>
              <v-form ref="valueForm">
                <v-flex xs12 v-for="cofactor in formCofactors" :key="cofactor.Id">
                  <v-layout row wrap>
                    <v-flex xs12>
                      <v-autocomplete :label="cofactor.IncludeCofactors.length > 1 ? 'CoFactors' : 'CoFactor'" v-bind:items="formCofactors" v-model="cofactor.Id" item-text="Name" item-value="Id" disabled :persistent-hint="cofactor.IncludeCofactors.length > 1" 
                      :hint="'Cofactors are linked to analyte ' + cofactor.LinkAnalyte.Name"></v-autocomplete>
                    </v-flex>
                    <template v-for="includeCofactor in cofactor.IncludeCofactors">
                      <v-flex xs12 v-if="includeCofactor.Description" :key="includeCofactor.Id">
                        <v-textarea auto-grow rows="1" :label="cofactor.IncludeCofactors.length > 1 ? 'Description for ' + includeCofactor.Name : 'Description'" v-model="includeCofactor.Description" disabled></v-textarea>
                      </v-flex>
                    </template>
                    <v-flex xs6 pr-2>
                      <v-text-field label="DefaultValue" v-model="cofactor.DefaultValue" :rules="formRuleValue">
                      </v-text-field>
                    </v-flex>
                    <v-flex xs6>
                      <v-autocomplete label="Unit" v-bind:items="allUnitsShow(cofactor)" v-model="cofactor.Unit" item-text="Name" item-value="Name" :rules="formRuleUnit"></v-autocomplete>
                    </v-flex>
                  </v-layout>
                  <br>
                </v-flex>
              </v-form>
              <v-flex xs12>
                <v-alert icon="info" color="warning" :value="wrongData">Data could not be empty, please check carefully.
                </v-alert>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="success" @click="clickAddDefaultValues()">
              <v-icon>done</v-icon>{{$t('Button.Save')}}
            </v-btn>
            <v-btn outline color="secondary" @click="cancel()">{{$t('Button.Cancel')}}</v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </app-modal>

    <ErrorDialog v-model="errorDialog.show" :data="errorDialog.data" :title="errorDialog.title"></ErrorDialog>
  </v-container>
</template>
<script>
  import {
    algorithms,
  } from '@/helpers/algorithms'
  import {
    createNamespacedHelpers,
  } from 'vuex'
  import FormTemplate from './FormTemplate'
  import StationDataWithFallbackStrategyForm from '@/components/CoFactorManage/CoFactor/Form/StationDataWithFallbackStrategyForm'
  import OverrideStrategyForm from '@/components/CoFactorManage/CoFactor/Form/OverrideStrategyForm'
  import FormValidationUtil from '@/components/mixins/FormValidationUtil'
  import SnackBarUtil from '@/components/mixins/SnackBarUtil'
  import ErrorDialog from '@/components/generic/ErrorDialog'
  import AppModal from '@/components/generic/AppModal'
  const cofactor = createNamespacedHelpers('cofactor')
  const guidelinesparameter = createNamespacedHelpers('guidelinesparameter')
  const authenticate = createNamespacedHelpers('authenticate')
  const unit = createNamespacedHelpers('unit')
  const analyte = createNamespacedHelpers('analyte')
  import {
    unitSelection,
  } from '@/helpers/unitSelection'

  const primaryWindowKey = 'primary'
  export default {
    components: {
      'form-template': FormTemplate,
      'override-strategy-form': OverrideStrategyForm,
      'station-data-strategy-form': StationDataWithFallbackStrategyForm,
      'app-modal': AppModal,
      ErrorDialog,
    },
    mixins: [FormValidationUtil, SnackBarUtil],
    props: {
      dialog: {
        type: Boolean,
        required: true,
      },
      value: {
        type: String,
        required: true,
      },
      originalValue: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        activate: false,
        selectedAlgorithm: '',
        isLoading: false,
        addDefaultValue: false,
        wrongData: false,
        window: primaryWindowKey,
        newDefaultValue: {},
        showSelectStrategy: true,
        formRuleValue: [
          v => {
            if(v) {
              return this.rules.numeric(v) || 'Value must be a number'
            } else {
              return 'Could not be null, please check again.'
            }
          },
        ],
        formRuleUnit: [
          v => !!v || 'Could not be null, please check again.',
        ],
        changeStrategy: '',
        formCofactors: [],
      }
    },
    computed: {
      ...cofactor.mapGetters(['cofactorList', 'strategyList', 'cofactorLinkAnalyte']),
      ...authenticate.mapGetters(['currentUserGroup']),
      ...unit.mapGetters(['allUnits', 'allUnitGroups']),
      ...analyte.mapGetters(['tenantAnalyteList']),
      algorithmList() {
        return Object.keys(algorithms).map(key => algorithms[key])
      },
      strategyOptions() {
        return this.algorithmList.map(a => {
          return {
            text: a.Name,
            value: a.Key,
          }
        })
      },
      selectedAlgorithmObject() {
        return this.algorithmList.find(x => x.Key == this.selectedAlgorithm)
      },
      selectedAlgorithmHasForm() {
        if (this.selectedAlgorithmObject == null) {
          return false
        }
        return this.selectedAlgorithmObject.FormComponent != null
      },
      formHasNext() {
        let target = this.strategyList.find(x => x.StrategyCode == this.selectedAlgorithm)
        let noDefaultValue = this.selectedAlgorithm == 'no-default-value-strategy'
        if (target && target.CoFactorDefaultValues) {
          let bool = this.cofactorLinkAnalyte.every(x =>
            target.CoFactorDefaultValues.some(y => y.CoFactorId == x.CoFactorId))
          return !bool
        } else if (noDefaultValue) {
          return false
        } else {
          return true
        }
      },
      isValidForm() {
        return this.value != this.originalValue && this.value != ''
      },
      errorDialog() {
        return this.$store.getters['app/errorDialog']
      },
    },
    watch: {
      dialog() {
        this.activate = this.dialog
      },
      activate() {
        if (!this.activate) {
          this.clearForm()
        }
        this.$emit('change', this.activate)
      },
      value() {
        this.selectedAlgorithm = this.value
      },
      selectedAlgorithm() {
        this.$emit('input', this.selectedAlgorithm)
        this.setFormCofactors()
      },
    },
    methods: {
      ...guidelinesparameter.mapActions(['UpdateTenantDefaultStrategy']),
      ...cofactor.mapActions(['addMissingCoFactorDefaultValue']),
      unitSelection,
      allUnitsShow(cofactor) {
        let target = this.cofactorLinkAnalyte.find(x => x.CoFactorId == cofactor.Id)
        return this.unitSelection(target, this.cofactorLinkAnalyte, this.tenantAnalyteList, this.allUnits, this.allUnitGroups, this.cofactorList, cofactor.Id)
      },
      setFormCofactors() {
        this.formCofactors = []
        if (this.strategyList.length) {
          let newCoFactorList = JSON.parse(JSON.stringify(this.cofactorList))
          let targetStrategy = this.strategyList.find(x => x.StrategyCode == this.selectedAlgorithm)
          if (targetStrategy) {
            let cofactors = newCoFactorList.map(coFactor => {
              let matched = targetStrategy.CoFactorDefaultValues.find(x => x.CoFactorId == coFactor.Id)
              if (matched) {
                return {
                  Id: matched.CoFactorId,
                  Name: matched.CoFactorName,
                  DefaultValue: matched.DefaultValue,
                  Unit: matched.Unit,
                  Description: matched.Description,
                }
              } else {
                return coFactor
              }
            })
            cofactors.forEach(cofactor => {
              let linkAnalyte = this.cofactorLinkAnalyte.find(x => x.CoFactorId == cofactor.Id)
              if (linkAnalyte.AnalyteId) {
                let matchCofactor = this.formCofactors.find(x => x.LinkAnalyte.Id == linkAnalyte.AnalyteId)
                if (matchCofactor) {
                  matchCofactor.Name = matchCofactor.Name.concat(', ', cofactor.Name)
                  matchCofactor.IncludeCofactors.push({
                    Id: cofactor.Id,
                    Name: cofactor.Name,
                    Description: cofactor.Description,
                  })
                  matchCofactor.Unit = !cofactor.Unit ? cofactor.Unit : matchCofactor.Unit
                  matchCofactor.DefaultValue = !cofactor.DefaultValue ? cofactor.DefaultValue : matchCofactor.DefaultValue
                  return true
                }
              }
              this.formCofactors.push({
                ...cofactor,
                LinkAnalyte: linkAnalyte.AnalyteId ? {
                  Id: linkAnalyte.AnalyteId,
                  Name: this.tenantAnalyteList.find(x => x.Id == linkAnalyte.AnalyteId).AnalyteName,
                } : { Id: linkAnalyte.AnalyteId },
                IncludeCofactors: [{
                  Id: cofactor.Id,
                  Name: cofactor.Name,
                  Description: cofactor.Description,
                }],
              })
            })
          } else {
            this.formCofactors = []
          }
        }
      },
      updateTenantStrategy() {
        let options = {
          data: {
            TenantId: this.currentUserGroup.id,
            StrategyCode: this.selectedAlgorithm,
          },
          successCb: () => {
            this.handleNotification({
              type: 'success',
              message: 'save success',
            })
            this.cancel()
          },
          failedCb: (err) => {
            this.$store.dispatch('app/setErrorDialog', {
              data: err.response.data.Message,
              show: true,
            })
          },
        }
        this.UpdateTenantDefaultStrategy(options)
      },
      clickSaveForm() {
        let noDefaultValue = this.selectedAlgorithm == 'no-default-value-strategy'
        if (noDefaultValue) {
          this.updateTenantStrategy()
          this.clearForm()
        } else {
          if (!this.formHasNext) {
            this.updateTenantStrategy()
            this.clearForm()
          } else {
            this.changeStrategy = this.selectedAlgorithm
            this.showSelectStrategy = false
            this.addDefaultValue = true
          }
        }
      },
      cancel() {
        this.clickClearForm()
        this.addDefaultValue = false
        this.showSelectStrategy = true
      },
      clickClearForm() {
        this.propagateCancel()
        this.clearForm()
      },
      clearForm() {
        this.window = primaryWindowKey
        this.activate = false
      },
      propagateSave() {
        this.$emit('save')
      },
      propagateCancel() {
        this.$emit('cancel')
      },
      propagateNotification(notification) {
        this.$emit('notify', notification)
      },
      clickAddDefaultValues() {
        let postCofactor = []
        this.formCofactors.forEach(cofactor => {
          cofactor.IncludeCofactors.forEach(includeCofactor => {
            postCofactor.push({
              Id: includeCofactor.Id,
              Name: includeCofactor.Name,
              DefaultValue: cofactor.DefaultValue,
              Unit: cofactor.Unit,
              Description: includeCofactor.Description,
            })
          })
        })
        let alert = !postCofactor.every(x => x.Id && x.Name && x.DefaultValue && x.Unit)
        if (alert) {
          this.$refs.valueForm.validate()
          this.wrongData = alert
        } else {
          let that = this
          this.wrongData = false
          let StrategyId
          let Strategy = this.strategyList.find(x => x.StrategyCode == this.selectedAlgorithm)
          StrategyId = Strategy.Id
          let CoFactorDefaultValues = []
          postCofactor.map(x => {
            let cofactor = {
              Id: null,
              CoFactorId: x.Id,
              CoFactorName: x.Name,
              DefaultValue: x.DefaultValue,
              Unit: x.Unit,
              TenantId: this.currentUserGroup.id,
              StrategyId: StrategyId,
            }
            CoFactorDefaultValues.push(cofactor)
          })
          let options = {
            data: {
              CoFactorDefaultValues,
              TenantId: this.currentUserGroup.id,
              StrategyId: StrategyId,
            },
            successCb() {
              that.updateTenantStrategy()
            },
            failedCb: (err) => {
              this.$store.dispatch('app/setErrorDialog', {
                data: err.response.data.Message,
                show: true,
              })
            },
          }
          this.addMissingCoFactorDefaultValue(options)
        }
      },
    },
  }
</script>
<style scoped lang="stylus"></style>
